import { buildContextoLibro } from "../cobro/resolverSaldo";
import { getSaldosIniciales } from "../saldosIniciales";
import { client } from "../supabase";
import { toDisplayCurrency } from "./currency";
import { construirLedgerProveedor } from "./ledgerProveedor";
import { encontrarSaldoInicial, FECHA_CORTE } from "./saldoInicial";

// R3.2 — eventos del libro del eje PAGO (recibidas/externas/boletas) para que `construirLedgerProveedor`
// derive la saldada/saldo por factura de `resolverSaldo` (regla 10.190). aplicaciones_saldo NO es
// país-scoped; se filtra por documento_tipo. Fuente única, sin reimplementar el saldo.
const LIBRO_PAGO_SELECT = "documento_tipo, documento_id, via, monto_documento, moneda_origen, fecha_evento";
const fetchCtxLibroPago = async () => {
  const { data, error } = await client
    .from("aplicaciones_saldo")
    .select(LIBRO_PAGO_SELECT)
    .in("documento_tipo", ["sii_recibida", "factura_externa", "boleta_honorario"]);
  if (error) throw error;
  return buildContextoLibro(data || []);
};

// Saldo FIFO pendiente por factura de un proveedor. FUENTE ÚNICA: reusa construirLedgerProveedor
// (el motor canónico del ledger de EstadoCuenta) — NO reimplementa el FIFO (regla 10.1). Solo
// orquesta el fetch de los mismos inputs que usa EstadoCuenta + la llamada al motor.
//
// Devuelve el `saldosPorFactura` del ledger: Map keyed "EXT:{id}" / "SII:{id}" → { saldoDisplay,
// factura }, que incluye SOLO las facturas con saldo > 0.01 (las totalmente amortizadas quedan
// fuera). `saldoDisplay` es el REMANENTE (no el total) tras aplicar pagos a cuenta (FIFO) + MBF +
// NCs (10.42/10.43). El saldo queda expresado en la moneda del proveedor (moneda_default) — misma
// convención que FlujoCajaV2 y el ledger de EstadoCuenta para un proveedor USD.
//
// Self-contained a propósito: el consumidor (selector de "Vincular existente") obtiene el saldo real
// lo abra quien lo abra (FlujoCajaV2, EstadoCuenta, MovimientosBancarios), sin depender del caller.
export const getSaldosPorFacturaProveedor = async (provId, pais) => {
  const monedaPrincipal = pais === "Perú" ? "PEN" : "CLP";

  const [provRes, cajaRes, extRes, siiRes, honRes, mbfRes, saldosIniciales, ctxLibro] = await Promise.all([
    client.from("proveedores").select("id, moneda_default").eq("id", provId).maybeSingle(),
    client
      .from("movimientos_caja")
      .select(
        "id, fecha, monto, moneda, tipo, entidad_tipo, entidad_id, descripcion, numero_documento, categoria_pago, estado, movimiento_bancario_id"
      )
      .eq("pais", pais)
      .neq("estado", "anulado")
      .gte("fecha", FECHA_CORTE),
    client
      .from("facturas_externas")
      .select(
        "id, proveedor_id, fecha_emision, monto_total, moneda, numero_documento, estado, concepto, archivo_url, periodo_mes, movimiento_bancario_id"
      )
      .eq("pais", pais),
    client
      .from("facturas_sii_recibidas")
      .select(
        "id, proveedor_id, fecha_emision, monto_total, folio, tipo_dte, razon_social_emisor, estado_vinculacion, residual_corte, tipo_relacion_nc, documento_referenciado_id"
      )
      .eq("pais", pais)
      // Pieza A: post-corte SIEMPRE + pre-corte SOLO con residual_corte > 0 (despiertan por folio; el
      // ledger descompone el saldo inicial agregado usando residual_corte). Ver regla 10.29 y 10.1.
      .or(`fecha_emision.gte.${FECHA_CORTE},residual_corte.gt.0`),
    // BHE (Fase 1): deuda del proveedor en el ledger FIFO (monto_bruto). Mismo corte que las SII
    // recibidas; el filtro `computa` vive en construirLedgerProveedor. Se acota al proveedor pedido.
    client
      .from("boletas_honorarios")
      .select("id, proveedor_id, folio, fecha_emision, monto_bruto, computa")
      .eq("pais", pais)
      .eq("proveedor_id", provId)
      .gte("fecha_emision", FECHA_CORTE),
    client.from("movimiento_bancario_facturas").select("movimiento_bancario_id, factura_tipo, factura_id, monto"),
    getSaldosIniciales(pais),
    fetchCtxLibroPago(),
  ]);

  for (const r of [provRes, cajaRes, extRes, siiRes, honRes, mbfRes]) {
    if (r.error) throw r.error;
  }

  const provMoneda = provRes.data?.moneda_default || monedaPrincipal;
  const saldoInicial = encontrarSaldoInicial(saldosIniciales || [], "proveedor", provId);
  // Conversión a la moneda del proveedor, con el util canónico (10.1) — mismo criterio que
  // EstadoCuenta (monedaSaldo) y FlujoCajaV2 (tdcProv) para un proveedor USD.
  const toDisp = (monto, monedaOrigen, fecha) =>
    toDisplayCurrency(monto, monedaOrigen, fecha, { monedaSaldo: provMoneda, monedaPrincipal, pais });

  const { saldosPorFactura } = construirLedgerProveedor({
    provId: Number(provId),
    siiRecibidas: siiRes.data || [],
    facturasExternas: extRes.data || [],
    honorarios: honRes.data || [],
    movimientosCaja: cajaRes.data || [],
    saldoInicial,
    monedaPrincipal,
    toDisplayCurrency: toDisp,
    vinculacionesDirectas: mbfRes.data || [],
    // Iguala el criterio de getFacturasRecibidas(estado_not:'ignorada') del reload (cargarDatosFuturos):
    // una factura 'ignorada' NO es deuda por pagar → fuera del ledger. Sin esto, el patch scoped
    // divergiría del reload para un proveedor con una 'ignorada' post-corte.
    excluirIgnoradas: true,
    ctxLibro, // R3.2: la saldada/saldo por factura sale del libro (resolverSaldo)
    // …pero SOLO para SII/BHE. Las EXT (facturas_externas) quedan en FIFO aun con ctxLibro (decisión
    // C1 / R3.2b): el libro no modela sus pagos por transferencia genérica → wirearlas a resolverSaldo
    // reabriría el fantasma "pendiente" (caso Ignacio) en el gate de VincularMovimientoModal. NO tocar.
  });

  return saldosPorFactura;
};

// Versión BATCH: saldo FIFO pendiente por factura SII recibida de TODOS los proveedores del país,
// en UNA sola pasada de fetch. Es la fuente idéntica a getSaldosPorFacturaProveedor (mismo motor
// construirLedgerProveedor, mismos inputs y mismo criterio de conversión) — cambia SOLO cuántos
// fetches hace: llamar el helper por-proveedor N veces re-fetchea los mismos datos country-wide N
// veces (85 proveedores en Chile ≈ ~595 queries). Acá se fetchea el universo UNA vez y se corre el
// motor puro en memoria por cada proveedor (mismo patrón que FlujoCajaV2). Regla 10.1 (no reimplementa
// el FIFO). NO pasa excluirProvIds ni facturasComisionFactoring — para que el resultado por factura
// sea idéntico al del helper por-proveedor que consume el modal de conciliación.
//
// Devuelve Map<facturaId (SII recibida), saldoDisplay> con SOLO las facturas con saldo pendiente
// (> 0.01). Las saldadas o no trackeadas por el ledger (pre-corte dormidas, NC) quedan fuera del map.
export const getSaldosRecibidasPorFactura = async (pais) => {
  const monedaPrincipal = pais === "Perú" ? "PEN" : "CLP";

  const [provsRes, cajaRes, extRes, siiRes, honRes, mbfRes, saldosIniciales, ctxLibro] = await Promise.all([
    client.from("proveedores").select("id, moneda_default").eq("pais", pais),
    client
      .from("movimientos_caja")
      .select(
        "id, fecha, monto, moneda, tipo, entidad_tipo, entidad_id, descripcion, numero_documento, categoria_pago, estado, movimiento_bancario_id"
      )
      .eq("pais", pais)
      .neq("estado", "anulado")
      .gte("fecha", FECHA_CORTE),
    client
      .from("facturas_externas")
      .select(
        "id, proveedor_id, fecha_emision, monto_total, moneda, numero_documento, estado, concepto, archivo_url, periodo_mes, movimiento_bancario_id"
      )
      .eq("pais", pais),
    client
      .from("facturas_sii_recibidas")
      .select(
        "id, proveedor_id, fecha_emision, monto_total, folio, tipo_dte, razon_social_emisor, estado_vinculacion, residual_corte, tipo_relacion_nc, documento_referenciado_id"
      )
      .eq("pais", pais)
      // Mismo filtro que getSaldosPorFacturaProveedor: post-corte SIEMPRE + pre-corte solo con residual.
      .or(`fecha_emision.gte.${FECHA_CORTE},residual_corte.gt.0`),
    // BHE del país entero (el filtro por proveedor lo hace construirLedgerProveedor internamente).
    client
      .from("boletas_honorarios")
      .select("id, proveedor_id, folio, fecha_emision, monto_bruto, computa")
      .eq("pais", pais)
      .gte("fecha_emision", FECHA_CORTE),
    client.from("movimiento_bancario_facturas").select("movimiento_bancario_id, factura_tipo, factura_id, monto"),
    getSaldosIniciales(pais),
    fetchCtxLibroPago(),
  ]);

  for (const r of [provsRes, cajaRes, extRes, siiRes, honRes, mbfRes]) {
    if (r.error) throw r.error;
  }

  const siiRecibidas = siiRes.data || [];
  const facturasExternas = extRes.data || [];
  const honorarios = honRes.data || [];
  const movimientosCaja = cajaRes.data || [];
  const vinculacionesDirectas = mbfRes.data || [];
  const monedaPorProv = new Map((provsRes.data || []).map((p) => [p.id, p.moneda_default || monedaPrincipal]));

  // Un motor por proveedor con alguna factura SII recibida en el universo fetcheado. construirLedgerProveedor
  // filtra internamente por provId, así que se le pasa el universo country-wide completo (igual que FlujoCajaV2).
  const provIds = [...new Set(siiRecibidas.map((f) => f.proveedor_id).filter((x) => x != null))];
  const saldos = new Map(); // facturaId → saldoDisplay (remanente FIFO)
  for (const provId of provIds) {
    const provMoneda = monedaPorProv.get(provId) || monedaPrincipal;
    const saldoInicial = encontrarSaldoInicial(saldosIniciales || [], "proveedor", provId);
    const toDisp = (monto, monedaOrigen, fecha) =>
      toDisplayCurrency(monto, monedaOrigen, fecha, { monedaSaldo: provMoneda, monedaPrincipal, pais });
    const { saldosPorFactura } = construirLedgerProveedor({
      provId: Number(provId),
      siiRecibidas,
      facturasExternas,
      honorarios,
      movimientosCaja,
      saldoInicial,
      monedaPrincipal,
      toDisplayCurrency: toDisp,
      vinculacionesDirectas,
      excluirIgnoradas: true, // idem getSaldosPorFacturaProveedor: 'ignorada' fuera del ledger (reload parity)
      ctxLibro, // R3.2: la saldada/saldo por factura sale del libro (resolverSaldo)
    });
    for (const [key, { saldoDisplay }] of saldosPorFactura) {
      if (key.startsWith("SII:")) saldos.set(Number(key.slice(4)), saldoDisplay);
    }
  }
  return saldos;
};
